import { Fragment, useState } from 'react';

import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

type Props = {
  extraData: unknown;
};

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map(v => String(v)).join(', ');
  }
  if (typeof value === 'object' && value != null) {
    return JSON.stringify(value);
  }
  return String(value);
};

const isEmpty = (value: unknown): boolean =>
  value == null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

export const ActivityExtraDataContent = ({ extraData }: Props) => {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);

  if (extraData == null || typeof extraData !== 'object') {
    return null;
  }

  const entries = Object.entries(extraData as Record<string, unknown>).filter(
    ([, v]) => !isEmpty(v),
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ mt: 0.5 }}>
      <Button
        size="small"
        disableRipple
        onClick={() => setExpanded(v => !v)}
        endIcon={expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        sx={{
          alignSelf: 'flex-start',
          p: 0,
          minWidth: 0,
          textTransform: 'none',
          color: 'text.secondary',
          fontSize: '0.75rem',
          '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
          '&:focus': { color: 'text.secondary' },
        }}
      >
        {expanded ? t('Show less') : t('Show more')}
      </Button>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, max-content) 1fr',
            columnGap: 1.5,
            rowGap: 1,
            mt: 0.5,
          }}
        >
          {entries.map(([key, value]) => (
            <Fragment key={key}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {key}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {formatValue(value)}
              </Typography>
            </Fragment>
          ))}
        </Box>
      </Collapse>
    </Stack>
  );
};
